/**
 * Board dates: a month's status reports are due, and lock, a set number of
 * working days before its DDI Board. The rules only suggest. Typing a board
 * date fills both dates; a date typed by hand is marked moved and kept when
 * the board date changes. Kept pure so the arithmetic is testable.
 */

import type { BoardDateRulesUpdate, ReportingWindow } from '../types'
import { fmtUk, londonDate, londonInstant, parseInstant } from './reportingWindow'
import { workingDaysBefore } from './workingDays'

/** UK wall-clock hour at which reports are due and lock. */
export const DEADLINE_HOUR = 17

export type Deadline = 'due' | 'lock'

/** One month's dates as edited, each YYYY-MM-DD or empty. A moved date was
 *  typed by hand, so a new board date leaves it where it is. */
export interface BoardDateDraft {
  boardDate: string
  dueDate: string
  lockDate: string
  dueMoved: boolean
  lockMoved: boolean
}

/** A date input reports every keystroke, so while the year is being typed the
 *  value passes through years like 0202. Only a whole, modern date is counted. */
export const isFullDate = (value: string | null | undefined): value is string =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= 2000

export const workingDaysFor = (which: Deadline, rules: BoardDateRulesUpdate): number =>
  which === 'due' ? rules.dueWorkingDaysBefore : rules.lockWorkingDaysBefore

/** The due and lock dates the rules give for a board date. */
export function ruleDates(boardDate: string, rules: BoardDateRulesUpdate): Record<Deadline, string> {
  return {
    due: workingDaysBefore(boardDate, rules.dueWorkingDaysBefore),
    lock: workingDaysBefore(boardDate, rules.lockWorkingDaysBefore),
  }
}

const dateOf = (draft: BoardDateDraft, which: Deadline) => (which === 'due' ? draft.dueDate : draft.lockDate)
const movedOf = (draft: BoardDateDraft, which: Deadline) => (which === 'due' ? draft.dueMoved : draft.lockMoved)

function setDate<T extends BoardDateDraft>(draft: T, which: Deadline, date: string, moved: boolean): T {
  return which === 'due' ? { ...draft, dueDate: date, dueMoved: moved } : { ...draft, lockDate: date, lockMoved: moved }
}

/** A new board date. A full date refills each date from its rule unless that
 *  date was moved by hand and still has a value. Anything short of a full
 *  date, cleared or half typed, leaves both dates and their marks alone. */
export function applyBoardDate<T extends BoardDateDraft>(draft: T, boardDate: string, rules: BoardDateRulesUpdate): T {
  let next = { ...draft, boardDate }
  if (!isFullDate(boardDate)) return next
  const rule = ruleDates(boardDate, rules)
  for (const which of ['due', 'lock'] as const) {
    if (!(movedOf(draft, which) && dateOf(draft, which))) next = setDate(next, which, rule[which], false)
  }
  return next
}

/** A date typed by hand into Reports due or Reports lock. */
export const moveDate = <T extends BoardDateDraft>(draft: T, which: Deadline, date: string): T =>
  setDate(draft, which, date, true)

/** Back onto the rule date, no longer moved. Needs a full board date. */
export function applyRule<T extends BoardDateDraft>(draft: T, which: Deadline, rules: BoardDateRulesUpdate): T {
  if (!isFullDate(draft.boardDate)) return draft
  return setDate(draft, which, ruleDates(draft.boardDate, rules)[which], false)
}

/** The rule date to offer under a moved date that is off it, or null. */
export function offeredRuleDate(draft: BoardDateDraft, which: Deadline, rules: BoardDateRulesUpdate): string | null {
  if (!isFullDate(draft.boardDate) || !movedOf(draft, which)) return null
  const rule = ruleDates(draft.boardDate, rules)[which]
  return dateOf(draft, which) === rule ? null : rule
}

/** The dates of a stored window. One saved before the moved marks existed
 *  counts a date as moved when it has a board date and the date is off its
 *  rule. */
export function draftFromWindow(window: ReportingWindow, rules: BoardDateRulesUpdate): BoardDateDraft {
  const boardDate = window.ddiBoardDate ?? ''
  const dueDate = window.dueAt ? londonDate(window.dueAt) : ''
  const lockDate = londonDate(window.closesAt)
  const rule = isFullDate(boardDate) ? ruleDates(boardDate, rules) : null
  return {
    boardDate, dueDate, lockDate,
    dueMoved: window.dueMoved ?? (!!rule && dueDate !== rule.due),
    lockMoved: window.lockMoved ?? (!!rule && lockDate !== rule.lock),
  }
}

/** 17:00 UK time on a date. */
export function deadlineAt(date: string): Date {
  const [y, m, d] = date.split('-').map(Number)
  return londonInstant(y, m, d, DEADLINE_HOUR)
}

/** Why a month's dates cannot be saved, or null when they can. The same
 *  checks, in the same words, as the server. */
export function windowProblem(month: string, { dueDate, lockDate }: Pick<BoardDateDraft, 'dueDate' | 'lockDate'>): string | null {
  if (!lockDate) return 'Set the date reports lock.'
  if (dueDate && dueDate > lockDate) return 'Reports cannot lock before they are due.'
  const first = `${month}-01`
  const opens = fmtUk(parseInstant(first), false)
  if (dueDate && dueDate < first) return `Reports would be due before the window opens on ${opens}.`
  if (lockDate < first) return `Reports would lock before the window opens on ${opens}.`
  return null
}

export const MAX_WORKING_DAYS = 30

/** Why the rules cannot be saved, or null when they can. The server's checks
 *  and words. */
export function rulesProblem({ dueWorkingDaysBefore: due, lockWorkingDaysBefore: lock }: BoardDateRulesUpdate): string | null {
  const allowed = (n: number) => Number.isInteger(n) && n >= 0 && n <= MAX_WORKING_DAYS
  if (!allowed(due) || !allowed(lock)) return `Use a whole number of working days from 0 to ${MAX_WORKING_DAYS}.`
  if (lock > due) return 'Reports cannot lock before they are due.'
  return null
}
